/**
 * @desc        Animal options for the animal selector
 * @version     0.0.1 (Added separate sections for animals and cohorts) - 23 January 2026
 * */
// Shared animal configuration blocks
// Extracted from animal-classes.js to keep classes lean and DRY

/** Numeric translation keys for species <select> display (option values stay English for persistence). */
export const SPECIES_SELECTOR_CATEGORY_KEYS = {
    Aquatic: '4677',
    Insects: '4678',
    Birds: '4679',
    Camelids: '4680',
    Cervids: '4681',
    Equids: '4682',
    Ruminants: '4683',
    Suids: '4684',
    'Coming Soon': '5113',
}

export const SPECIES_SELECTOR_SPECIES_KEYS = {
    Fish: '4685',
    Bees: '4686',
    Chickens: '4687',
    Ducks: '4688',
    Emus: '4689',
    Geese: '4690',
    Ostriches: '4691',
    Turkeys: '4692',
    Alpacas: '4693',
    Camels: '4694',
    Llamas: '4695',
    Deer: '4696',
    Horses: '4697',
    Cattle: '1813',
    Goats: '4176',
    Sheep: '1812',
    Pigs: '4175',
    Alligator: '5131',
    Buffalo: '5132',
    Crocodile: '5133',
    Gamebirds: '5134',
    Rabbit: '5135',
    Reindeer: '5136',
    'Wapiti/Elk': '5137',
    Yak: '5138',
}

export const ANIMAL_OPTIONS = [
    // { label: 'Aquatic', options: ['Fish'] },
    // { label: 'Insects', options: ['Bees'] },
    // { label: 'Birds', options: ['Chickens', 'Ducks', 'Emus', 'Geese', 'Ostriches', 'Turkeys'] },
    { label: 'Camelids', options: ['Alpacas', 'Camels', 'Llamas'] },
    { label: 'Cervids', options: ['Deer'] },
    // { label: 'Equids', options: ['Horses'] },
    { label: 'Ruminants', options: ['Cattle', 'Goats', 'Sheep'] },
    { label: 'Suids', options: ['Pigs'] },
    { label: 'Coming Soon', options: ['Alligator', 'Bees', 'Buffalo', 'Chickens', 'Crocodile', 'Ducks', 'Emus', 'Fish', 'Gamebirds', 'Geese', 'Horses', 'Kangaroo', 'Ostriches', 'Rabbit', 'Reindeer', 'Turkeys', 'Wapiti/Elk', 'Yak'], disabled: true },
]

export const ANIMAL_GENDER_OPTIONS = ['Male', 'Female']

export const BASE_SECTIONS = {
    Details: {
        name: '4824',
        description: '4825',
        icon: 'fad fa-info-square',
        ref: 'Details',
    },
    Identification: {
        name: '2058',
        description: '4826',
        icon: 'fad fa-id-card',
        ref: 'Identification',
    },
    Traits: {
        name: '4827',
        description: '4828',
        icon: 'fad fa-dna',
        ref: 'Traits',
    },
    Production: {
        name: '4829',
        description: '4830',
        icon: 'fad fa-weight',
        ref: 'Production',
    },
    Weaning: {
        name: '4831',
        description: '4832',
        icon: 'fad fa-birthday-cake',
        ref: 'Weaning',
    },
    Animals: {
        name: '4833',
        description: '4834',
        icon: 'fad fa-list',
        ref: 'Animals',
    },
    Birthing: {
        name: '4835',
        description: '4836',
        icon: 'fad fa-birthday-cake',
        ref: 'Birthing',
    },
    GroupAnimals: {
        name: '4837',
        description: '4838',
        icon: 'fad fa-list',
        ref: 'GroupAnimals',
    },
}

export const FORM_SECTIONS = {
    animal: {
        GroupAnimals: { ...BASE_SECTIONS.GroupAnimals },
        Details: { ...BASE_SECTIONS.Details },
        Birthing: { ...BASE_SECTIONS.Birthing },
        Traits: { ...BASE_SECTIONS.Traits },
        Identification: { ...BASE_SECTIONS.Identification },
        Production: { ...BASE_SECTIONS.Production },
        Weaning: { ...BASE_SECTIONS.Weaning },
    },
    // cohort: {
    //     Details: { ...BASE_SECTIONS.Details, description: '4839' },
    //     Birthing: { ...BASE_SECTIONS.Birthing },
    //     Traits: { ...BASE_SECTIONS.Traits },
    //     Identification: { ...BASE_SECTIONS.Identification },
    //     // Weight: { ...BASE_SECTIONS.Weight },
    //     // Weaning: { ...BASE_SECTIONS.Weaning }, // not applicable for cohorts
    //     Animals: { ...BASE_SECTIONS.Animals },
    // },
    group: {
        GroupAnimals: { ...BASE_SECTIONS.GroupAnimals },
        Details: { ...BASE_SECTIONS.Details, description: '4839' },
        Birthing: { ...BASE_SECTIONS.Birthing },
        Traits: { ...BASE_SECTIONS.Traits },
        Identification: { ...BASE_SECTIONS.Identification },
        Production: { ...BASE_SECTIONS.Production },
        // Weight: { ...BASE_SECTIONS.Weight },
        // Weaning: { ...BASE_SECTIONS.Weaning }, // not applicable for cohorts
        Animals: { ...BASE_SECTIONS.Animals },
    },
}

export function encodeSpeciesCode(species, opts = {}) {
    const { allowDisabled = false } = opts
    const s = String(species ?? '').trim()
    if (!s) throw new Error('species is required')

    const sNorm = normaliseName(s)

    for (const group of ANIMAL_OPTIONS) {
        if (!allowDisabled && group.disabled) continue
        const groupInitial = (group.label?.trim() ?? '').charAt(0).toUpperCase()
        if (!groupInitial) continue

        for (const option of group.options ?? []) {
            const opt = String(option ?? '').trim()
            if (!opt) continue
            if (normaliseName(opt) === sNorm) return `${groupInitial}${opt.charAt(0).toUpperCase()}`
        }
    }

    throw new Error(`Unknown species: "${s}"`)
}

function normaliseName(name) {
    return String(name).trim().toLowerCase().replace(/\s+/g, ' ')
}
//TODO: move weight params into production
